import { useState } from "react";
import { motion } from "framer-motion";
import {
  Layers, Boxes, Building2, FlaskConical, Ruler, Droplets,
  Beaker, Sigma, ScanLine, HardHat, ArrowUpRight,
} from "lucide-react";
import { SERVICES } from "../../data";

const ICONS = { Layers, Boxes, Building2, FlaskConical, Ruler, Droplets, Beaker, Sigma, ScanLine, HardHat };

function Card({ s, i }) {
  const [hover, setHover] = useState(false);
  const Icon = ICONS[s.icon] || Building2;
  return (
    <motion.div
      data-testid={`service-card-${i}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: (i % 4) * 0.06 }}
      className="group relative bg-[#111111] hover:bg-[#1a1a1a] transition-colors duration-500 p-7 md:p-8 min-h-[240px] flex flex-col justify-between overflow-hidden"
    >
      {/* macro image reveal */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <img
          src={s.img}
          alt={s.title}
          loading="lazy"
          className={`h-full w-full object-cover transition-all duration-700 ease-out ${
            hover ? "opacity-30 scale-105" : "opacity-0 scale-100"
          }`}
          style={{ filter: "grayscale(0.3) contrast(1.1)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/40 to-transparent" />
      </div>

      <div className="relative z-10 flex items-start justify-between">
        <span className="font-mono text-[11px] tracking-[0.2em] text-[#ff4500]">{s.num}</span>
        <Icon size={26} strokeWidth={1.4} className="text-[#8e8e93] group-hover:text-[#ff4500] transition-colors duration-400" />
      </div>

      <div className="relative z-10">
        <h3 className="font-heading font-bold uppercase tracking-tight text-xl md:text-2xl text-[#f4f4f5] mb-2 flex items-center gap-2">
          {s.title}
          <ArrowUpRight size={18} className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-400 text-[#ff4500]" />
        </h3>
        <p className="text-[#8e8e93] text-sm font-light leading-relaxed max-w-xs">{s.desc}</p>
      </div>
    </motion.div>
  );
}

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="py-24 md:py-36">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#ff4500]">/ 01 — What we test</span>
            <h2 className="mt-5 font-heading font-black uppercase tracking-tighter leading-none text-4xl md:text-6xl text-[#f4f4f5]">
              One-stop solution to<br />all your testing needs.
            </h2>
          </div>
          <p className="max-w-sm text-[#8e8e93] font-light leading-relaxed">
            A complete material testing suite delivered under one accredited roof — from the ground beneath
            to the steel above.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-px bg-[#27272a] border border-[#27272a]">
          {SERVICES.map((s, i) => (
            <Card key={s.num} s={s} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
